import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Tag, Space, Input, Select, Modal, Form, message, Statistic, Row, Col, Descriptions, Timeline, DatePicker } from 'antd';
import { PlusOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const API = '/api/crm';
const typeMap = { 0: { text: '潜在客户', color: 'gold' }, 1: { text: '意向客户', color: 'blue' }, 2: { text: '成交客户', color: 'green' } };

export default function CrmManagement() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(undefined);
  const [keyword, setKeyword] = useState('');
  const [stats, setStats] = useState({ total: 0, potential: 0, interested: 0, closed: 0 });
  const [formVisible, setFormVisible] = useState(false);
  const [editId, setEditId] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [followVisible, setFollowVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => { loadList(); loadStats(); }, [page, filterType, keyword]);

  const loadList = async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: 20 };
      if (filterType !== undefined) params.type = filterType;
      if (keyword) params.keyword = keyword;
      const res = await axios.get(`${API}/customers`, { params });
      if (res.data?.code === 0) { setList(res.data.data.list); setTotal(res.data.data.total); }
    } catch (e) { message.error('加载失败'); }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      if (res.data?.code === 0) setStats(res.data.data);
    } catch (e) {}
  };

  const loadDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/customers/${id}`);
      if (res.data?.code === 0) { setDetail(res.data.data); setDetailVisible(true); }
    } catch (e) { message.error('加载详情失败'); }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editId) {
        await axios.put(`${API}/customers/${editId}`, values);
        message.success('修改成功');
      } else {
        await axios.post(`${API}/customers`, values);
        message.success('创建成功');
      }
      setFormVisible(false); form.resetFields(); setEditId(null);
      loadList(); loadStats();
    } catch (e) { message.error('操作失败'); }
  };

  const handleDelete = async (id) => {
    Modal.confirm({ title: '确认删除？', content: '将同时删除该客户所有跟进记录', onOk: async () => {
      await axios.delete(`${API}/customers/${id}`);
      message.success('删除成功'); loadList(); loadStats();
    }});
  };

  const handleFollowUp = async () => {
    try {
      const values = await form.validateFields(['content', 'follow_type', 'next_follow_date']);
      await axios.post(`${API}/customers/${detail.id}/follow-ups`, values);
      message.success('跟进已添加'); setFollowVisible(false);
      form.resetFields(['content', 'follow_type', 'next_follow_date']);
      loadDetail(detail.id); loadList();
    } catch (e) { message.error('添加跟进失败'); }
  };

  const columns = [
    { title: '客户名称', dataIndex: 'name', key: 'name' },
    { title: '联系人', dataIndex: 'contact_name', key: 'contact_name' },
    { title: '电话', dataIndex: 'contact_phone', key: 'contact_phone' },
    { title: '类型', dataIndex: 'type', key: 'type', render: v => <Tag color={typeMap[v]?.color}>{typeMap[v]?.text}</Tag> },
    { title: '来源', dataIndex: 'source', key: 'source' },
    { title: '最近跟进', dataIndex: 'last_follow_up', key: 'last_follow_up', render: v => v ? new Date(v).toLocaleDateString() : '-' },
    { title: '操作', key: 'action', render: (_, r) => (
      <Space>
        <Button size="small" onClick={() => loadDetail(r.id)}>详情</Button>
        <Button size="small" onClick={() => { setEditId(r.id); form.setFieldsValue(r); setFormVisible(true); }}>编辑</Button>
        <Button size="small" danger onClick={() => handleDelete(r.id)}>删除</Button>
      </Space>
    )}
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="全部客户" value={stats.total} /></Card></Col>
        <Col span={6}><Card><Statistic title="潜在客户" value={stats.potential} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="意向客户" value={stats.interested} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="成交客户" value={stats.closed} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="搜索客户" prefix={<SearchOutlined />} value={keyword} onChange={e => setKeyword(e.target.value)} style={{ width: 240 }} allowClear />
          <Select placeholder="客户类型" value={filterType} onChange={setFilterType} allowClear style={{ width: 120 }}>
            <Select.Option value={0}>潜在客户</Select.Option>
            <Select.Option value={1}>意向客户</Select.Option>
            <Select.Option value={2}>成交客户</Select.Option>
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => { loadList(); loadStats(); }}>刷新</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditId(null); form.resetFields(); setFormVisible(true); }}>新增客户</Button>
        </Space>
        <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
          pagination={{ current: page, total, pageSize: 20, onChange: setPage }} />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal title={editId ? '编辑客户' : '新增客户'} open={formVisible} onOk={handleSubmit} onCancel={() => { setFormVisible(false); setEditId(null); }}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="客户名称" rules={[{ required: true }]}> <Input /> </Form.Item>
          <Form.Item name="contact_name" label="联系人"> <Input /> </Form.Item>
          <Form.Item name="contact_phone" label="联系电话"> <Input /> </Form.Item>
          <Form.Item name="address" label="地址"> <Input /> </Form.Item>
          <Form.Item name="type" label="客户类型" initialValue={0}>
            <Select><Select.Option value={0}>潜在客户</Select.Option><Select.Option value={1}>意向客户</Select.Option><Select.Option value={2}>成交客户</Select.Option></Select>
          </Form.Item>
          <Form.Item name="source" label="来源"> <Input /> </Form.Item>
          <Form.Item name="remark" label="备注"> <Input.TextArea /> </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal title="客户详情" open={detailVisible} onCancel={() => setDetailVisible(false)} width={640} footer={[
        <Button key="follow" type="primary" onClick={() => setFollowVisible(true)}>添加跟进</Button>,
        <Button key="close" onClick={() => setDetailVisible(false)}>关闭</Button>
      ]}>
        {detail && (
          <>
            <Descriptions column={2} bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="客户名称">{detail.name}</Descriptions.Item>
              <Descriptions.Item label="类型"><Tag color={typeMap[detail.type]?.color}>{typeMap[detail.type]?.text}</Tag></Descriptions.Item>
              <Descriptions.Item label="联系人">{detail.contact_name}</Descriptions.Item>
              <Descriptions.Item label="电话">{detail.contact_phone}</Descriptions.Item>
              <Descriptions.Item label="地址" span={2}>{detail.address}</Descriptions.Item>
              <Descriptions.Item label="来源">{detail.source}</Descriptions.Item>
              <Descriptions.Item label="备注">{detail.remark}</Descriptions.Item>
            </Descriptions>
            <div style={{ fontWeight: 'bold', marginBottom: 8 }}>跟进记录</div>
            <Timeline items={(detail.followUps || []).map(f => ({
              color: f.follow_type === 'visit' ? 'green' : f.follow_type === 'call' ? 'blue' : 'gray',
              children: <div><div style={{ fontSize: 12, color: '#999' }}>{new Date(f.created_at).toLocaleString()} · {f.follow_type === 'visit' ? '上门' : f.follow_type === 'call' ? '电话' : f.follow_type === 'wechat' ? '微信' : '其他'}</div><div>{f.content}</div>{f.next_follow_date && <div style={{ color: '#faad14', fontSize: 12 }}>下次跟进：{f.next_follow_date}</div>}</div>
            }))} />
          </>
        )}
      </Modal>

      {/* 跟进弹窗 */}
      <Modal title="添加跟进" open={followVisible} onOk={handleFollowUp} onCancel={() => setFollowVisible(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="follow_type" label="跟进方式" initialValue="visit">
            <Select><Select.Option value="visit">上门拜访</Select.Option><Select.Option value="call">电话沟通</Select.Option><Select.Option value="wechat">微信联系</Select.Option><Select.Option value="other">其他</Select.Option></Select>
          </Form.Item>
          <Form.Item name="content" label="跟进内容" rules={[{ required: true }]}> <Input.TextArea rows={4} /> </Form.Item>
          <Form.Item name="next_follow_date" label="下次跟进日期"> <DatePicker style={{ width: '100%' }} /> </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
